import Config from "src/config";
import { api, Api } from "../MainService/api";
import { ErrorKind } from "../MainService/apiProblem";
import {
  Prices,
  UserPlan,
  CouponWithPromoCodes,
  ProductWithPrices,
  Product,
} from "src/interfaces/Payments";

const PAYMENTS_URL = Config.PAYMENTS_URL;
const ADMIN_PAYMENTS_URL = Config.ADMIN_URL;
export class PaymentApi {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  // ========================
  // Existing Payment Methods
  // ========================
  async getPrices() {
    try {
      const response = await this.api.apisauce.get<Prices>(
        `${PAYMENTS_URL}/prices`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getCurrentPlan() {
    try {
      const response = await this.api.apisauce.get<UserPlan>(
        `${PAYMENTS_URL}/current-plan`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async createPaymentSession(priceId: string) {
    try {
      const response = await this.api.apisauce.get<{ url: string }>(
        `${PAYMENTS_URL}/create-payment-session?priceId=${priceId}`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  // ========================
  // Product and Price APIs
  // ========================
  // List all products with their associated prices
  async getProductsWithPrices() {
    try {
      const response = await this.api.apisauce.get<ProductWithPrices>(
        `${ADMIN_PAYMENTS_URL}/list-products-with-prices`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  // Create a new price for a specific product
  async createPriceForProduct(payload: {
    product_id: string;
    product_type: string;
    unit_amount: number;
    billing_cycle: "monthly" | "yearly";
  }) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_PAYMENTS_URL}/create-price?product_id=${payload.product_id}&product_type=${payload.product_type}&unit_amount=${payload.unit_amount}&billing_cycle=${payload.billing_cycle}`,
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  // Activate or deactivate a price
  async togglePriceStatus(priceId: string, activate: boolean) {
    try {
      const endpoint = activate
        ? `${ADMIN_PAYMENTS_URL}/activate-price?price_id=${priceId}`
        : `${ADMIN_PAYMENTS_URL}/deactivate-price?price_id=${priceId}`;

      const response = await this.api.apisauce.post(endpoint);
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  // ========================
  // Coupon and Promo Code APIs
  // ========================
  async createCoupon(payload: {
    name: string;
    percent_off?: number;
    amount_off?: number;
  }) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_PAYMENTS_URL}/create-coupon`,
        payload
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async createPromoCode(payload: {
    coupon_id: string;
    code: string;
    max_redemptions?: number;
    expires_at?: number;
  }) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_PAYMENTS_URL}/create-promo-code`,
        payload
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async activatePromoCode(promoCodeId: string) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_PAYMENTS_URL}/activate-promo-code?promo_code_id=${promoCodeId}`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async deactivatePromoCode(promoCodeId: string) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_PAYMENTS_URL}/deactivate-promo-code?promo_code_id=${promoCodeId}`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getCouponsWithPromoCodes() {
    try {
      const response = await this.api.apisauce.get<CouponWithPromoCodes>(
        `${ADMIN_PAYMENTS_URL}/list-coupons-with-promo-codes`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getProducts() {
    try {
      const response = await this.api.apisauce.get<{ products: Product[] }>(
        `${ADMIN_PAYMENTS_URL}/list-products`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async setDefaultPrice(priceId: string, productId: string) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_PAYMENTS_URL}/set-price-to-default?price_id=${priceId}&product_id=${productId}`
      );
      return await this.api.getResponse(response, true);
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }
}

export const paymentService = new PaymentApi(api);